import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { BottomNav, TopBar } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  RefreshCw, DollarSign, Clock, UserX, AlertTriangle,
  TrendingUp, XCircle, Building2, Users
} from 'lucide-react';
import { toast } from 'sonner';

const AccountabilitySnapshot = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.getAccountabilitySnapshot();
      setData(response.data);
    } catch (error) {
      console.error('Failed to load accountability data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '₹0';
    if (amount >= 10000000) {
      const crValue = amount / 10000000;
      return `₹${crValue % 1 === 0 ? crValue.toFixed(0) : parseFloat(crValue.toFixed(2))}Cr`;
    }
    if (amount >= 100000) {
      const lValue = amount / 100000;
      return `₹${lValue % 1 === 0 ? lValue.toFixed(0) : parseFloat(lValue.toFixed(1))}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getRateColor = (rate, type) => {
    if (type === 'conversion') {
      if (rate >= 20) return 'text-emerald-600 bg-emerald-50';
      if (rate >= 10) return 'text-amber-600 bg-amber-50';
      return 'text-[#5B3DF5] bg-red-50';
    }
    if (type === 'cancel') {
      if (rate <= 5) return 'text-emerald-600 bg-emerald-50';
      if (rate <= 15) return 'text-amber-600 bg-amber-50';
      return 'text-[#5B3DF5] bg-red-50';
    }
    return 'text-slate-600 bg-slate-50';
  };

  const getAlertColor = (count, threshold) => {
    if (count === 0) return 'text-emerald-600';
    if (count <= threshold) return 'text-amber-600';
    return 'text-[#5B3DF5]';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pb-nav">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading accountability data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-nav">
      <TopBar 
        title="Accountability Snapshot" 
        subtitle="MTD Performance Overview" 
        user={user} 
      />

      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="text-slate-600"
          >
            <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {data && (
          <>
            {/* Overall Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign size={16} className="text-emerald-600" />
                  <span className="text-xs text-slate-500 font-medium">Net Sales (MTD)</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(data.overall.net_sales)}
                </p>
              </Card>
              
              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-amber-600" />
                  <span className="text-xs text-slate-500 font-medium">Pending Approvals</span>
                </div>
                <p className={`text-2xl font-bold ${getAlertColor(data.overall.pending_approvals, 5)}`}>
                  {data.overall.pending_approvals}
                </p>
              </Card>
              
              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <UserX size={16} className="text-[#5B3DF5]" />
                  <span className="text-xs text-slate-500 font-medium">Zero Bookings</span>
                </div>
                <p className={`text-2xl font-bold ${getAlertColor(data.overall.reps_zero_bookings, 3)}`}>
                  {data.overall.reps_zero_bookings}
                  <span className="text-sm font-normal text-slate-400">/{data.overall.total_reps}</span>
                </p>
              </Card>
              
              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-orange-500" />
                  <span className="text-xs text-slate-500 font-medium">Inactive 7d</span>
                </div>
                <p className={`text-2xl font-bold ${getAlertColor(data.overall.reps_inactive_7d, 2)}`}>
                  {data.overall.reps_inactive_7d}
                  <span className="text-sm font-normal text-slate-400">/{data.overall.total_reps}</span>
                </p>
              </Card>
            </div>

            {/* Rates Summary */}
            <div className="grid grid-cols-2 gap-3">
              <Card className={`p-4 border-0 shadow-sm rounded-xl ${getRateColor(data.overall.conversion_rate, 'conversion')}`}>
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={16} />
                  <span className="text-xs font-medium opacity-80">Conversion Rate</span>
                </div>
                <p className="text-3xl font-bold">{data.overall.conversion_rate}%</p>
                <p className="text-xs opacity-70 mt-1">
                  {data.overall.total_bookings_mtd} bookings from CRM
                </p>
              </Card>
              
              <Card className={`p-4 border-0 shadow-sm rounded-xl ${getRateColor(data.overall.cancel_rate, 'cancel')}`}>
                <div className="flex items-center gap-2 mb-1">
                  <XCircle size={16} />
                  <span className="text-xs font-medium opacity-80">Cancel Rate</span>
                </div>
                <p className="text-3xl font-bold">{data.overall.cancel_rate}%</p>
                <p className="text-xs opacity-70 mt-1">
                  {data.overall.cancelled_count} of {data.overall.total_bookings_mtd} cancelled
                </p>
              </Card>
            </div>

            {/* City-wise Table */}
            <Card className="bg-white border-0 shadow-sm rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 size={16} className="text-slate-500" />
                  <h3 className="font-semibold text-slate-900">City-wise Breakdown</h3>
                </div>
                <span className="text-xs text-slate-400">{data.by_city.length} cities</span>
              </div>
              
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">City</th>
                      <th className="text-right px-3 py-3 font-semibold text-slate-600">Net Sales</th>
                      <th className="text-center px-3 py-3 font-semibold text-slate-600">
                        <span className="flex items-center justify-center gap-1">
                          <Clock size={12} />
                          Pending
                        </span>
                      </th>
                      <th className="text-center px-3 py-3 font-semibold text-slate-600">
                        <span className="flex items-center justify-center gap-1">
                          <UserX size={12} />
                          Zero
                        </span>
                      </th>
                      <th className="text-center px-3 py-3 font-semibold text-slate-600">
                        <span className="flex items-center justify-center gap-1">
                          <AlertTriangle size={12} />
                          7d
                        </span>
                      </th>
                      <th className="text-center px-3 py-3 font-semibold text-slate-600">Conv%</th>
                      <th className="text-center px-3 py-3 font-semibold text-slate-600">Cancel%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.by_city.map((city, idx) => (
                      <tr 
                        key={city.city} 
                        className={`hover:bg-slate-50 transition-colors ${idx === 0 ? 'bg-emerald-50/30' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                            }`}>
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-slate-900">{city.city}</p>
                              <p className="text-xs text-slate-400">{city.total_reps} reps</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <p className="font-semibold text-slate-900">{formatCurrency(city.net_sales)}</p>
                          <p className="text-xs text-slate-400">{city.approved_count} approved</p>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold ${
                            city.pending_approvals === 0 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : city.pending_approvals <= 3 
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                          }`}>
                            {city.pending_approvals}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold ${
                            city.reps_zero_bookings === 0 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : city.reps_zero_bookings <= 2 
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                          }`}>
                            {city.reps_zero_bookings}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold ${
                            city.reps_inactive_7d === 0 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : city.reps_inactive_7d <= 1 
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                          }`}>
                            {city.reps_inactive_7d}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                            getRateColor(city.conversion_rate, 'conversion')
                          }`}>
                            {city.conversion_rate}%
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                            getRateColor(city.cancel_rate, 'cancel')
                          }`}>
                            {city.cancel_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Totals Row */}
                  <tfoot className="bg-slate-100 border-t-2 border-slate-200">
                    <tr className="font-semibold">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Users size={16} className="text-slate-600" />
                          <span className="text-slate-900">Total</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right text-slate-900">
                        {formatCurrency(data.overall.net_sales)}
                      </td>
                      <td className="px-3 py-3 text-center text-slate-900">
                        {data.overall.pending_approvals}
                      </td>
                      <td className="px-3 py-3 text-center text-slate-900">
                        {data.overall.reps_zero_bookings}
                      </td>
                      <td className="px-3 py-3 text-center text-slate-900">
                        {data.overall.reps_inactive_7d}
                      </td>
                      <td className="px-3 py-3 text-center text-slate-900">
                        {data.overall.conversion_rate}%
                      </td>
                      <td className="px-3 py-3 text-center text-slate-900">
                        {data.overall.cancel_rate}%
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>

            {/* Footer */}
            <p className="text-center text-xs text-slate-400">
              MTD • As of {new Date(data.as_of).toLocaleString('en-IN', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </>
        )}
      </div>

      <BottomNav user={user} />
    </div>
  );
};

export default AccountabilitySnapshot;
